/* eslint-disable react/no-unescaped-entities */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import LanguageHelper from "@/components/LanguageHelper";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Home, MapPin, ShieldCheck, FileText, Wallet, AlertTriangle,
  Globe2, ListChecks, ArrowRight, Building2, Users, BadgeCheck, Sparkles,
} from "lucide-react";

/**
 * Pillar SEO page — Logement étudiant en France 2026.
 * Long-form (>3500 mots), structurée H1 → H2 → H3 → FAQ.
 * Maillage interne fort vers tous les satellites du silo Logement.
 * JSON-LD Article + FAQPage + BreadcrumbList pour position 0 Google.
 */
export default function PillarLogement() {
  const pageUrl = "https://studenthubfrance.fr/guide/logement-etudiant-france";
  const cover = "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1600&h=900&fit=crop";

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        "mainEntityOfPage": pageUrl,
        "headline": "Logement étudiant en France 2026 : le guide complet",
        "description": "Le guide ultime du logement étudiant en France en 2026 : CROUS, colocation, résidences privées, Visale, DossierFacile, APL, anti-arnaques, étudiants étrangers, checklist complète.",
        "datePublished": "2026-05-15",
        "dateModified": "2026-05-15",
        "author": { "@type": "Organization", "name": "Student Hub France", "url": "https://studenthubfrance.fr" },
        "publisher": {
          "@type": "Organization",
          "name": "Student Hub France",
          "logo": { "@type": "ImageObject", "url": "https://studenthubfrance.fr/logo.svg" },
        },
        "image": cover,
        "inLanguage": "fr-FR",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://studenthubfrance.fr/" },
          { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://studenthubfrance.fr/guide" },
          { "@type": "ListItem", "position": 3, "name": "Logement étudiant en France 2026" },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Combien coûte un logement étudiant en France en 2026 ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Le loyer moyen d'un étudiant en France en 2026 est de 480 €/mois en province et 750 €/mois à Paris (charges comprises). Un studio CROUS démarre à 175 €/mois, un T1 privé en province à 380 €, un studio à Paris à 700 €. L'APL peut réduire ce coût de 100 à 280 € selon ta zone et ton loyer." },
          },
          {
            "@type": "Question",
            "name": "Quand commencer à chercher son logement étudiant ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Pour septembre, démarre dès le mois de mai (résidences CROUS et privées ouvrent leurs réservations). Pour les zones tendues (Paris, Lyon, Bordeaux), commence en avril. En cours d'année, mi-décembre et fin juin sont les meilleures fenêtres (départs Erasmus, fins de baux)." },
          },
          {
            "@type": "Question",
            "name": "Peut-on louer un logement étudiant sans garant ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Oui. Active la garantie Visale gratuite (Action Logement) : elle remplace le garant pour 90 % des bailleurs. Le CROUS n'exige pas de garant. Tu peux aussi utiliser une caution privée payante (Garantme, SmartGarant, Cautioneo) ou bloquer une caution bancaire." },
          },
          {
            "@type": "Question",
            "name": "Quel est le délai pour toucher l'APL étudiant ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Compte 4 à 6 semaines entre le dépôt de ta demande CAF et le premier versement. L'APL est rétroactive au mois de demande. Délai plus long (8-12 semaines) pour les étudiants étrangers (vérification du titre de séjour)." },
          },
          {
            "@type": "Question",
            "name": "Comment éviter les arnaques au logement étudiant ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Visite toujours physiquement avant de signer. Ne paye JAMAIS le dépôt ou les frais avant la signature physique du bail. Vérifie le DPE, l'identité du bailleur (extrait de Kbis pour une société). Ignore les annonces avec propriétaire « à l'étranger » ou demandes de paiement Western Union." },
          },
          {
            "@type": "Question",
            "name": "Un étudiant étranger peut-il louer en France ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Oui, à condition d'avoir un titre de séjour valide (VLS-TS étudiant validé sur l'ANEF). Visale est accessible aux étudiants étrangers de 18 à 30 ans. Le CROUS accueille les étudiants internationaux. Les résidences privées (Studapart, Nemea) acceptent les profils internationaux avec dossier complet." },
          },
        ],
      },
    ],
  }), []);

  useSeo({
    title: "Logement étudiant en France 2026 : le guide complet | Student Hub",
    description: "Le guide ultime du logement étudiant en France 2026 : CROUS, colocation, résidences privées, Visale, DossierFacile, APL, anti-arnaques, étudiants étrangers. Tout en une page.",
    canonical: pageUrl,
    ogImage: cover,
    jsonLd,
    lang: "fr",
  });

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="pillar-logement">
      <AccessibilityToolbar />
      {/* Header */}
      <header className="relative z-40 max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="pillar-logement-home-link">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-violet-600 text-white shadow-md shadow-violet-300/40">
            <GraduationCap className="w-5 h-5" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight">Student Hub</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageHelper />
          <Link to="/blog" className="text-sm text-slate-700 hover:text-violet-700 hidden sm:inline-flex items-center gap-1">
            Blog <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/" data-testid="pillar-logement-cta-header">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full">Commencer</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 pb-12">
        <nav className="text-sm text-slate-500 mb-6" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-violet-700">Accueil</Link>
          <span className="mx-2">/</span>
          <span className="text-slate-400">Guides</span>
          <span className="mx-2">/</span>
          <span className="text-slate-700">Logement étudiant 2026</span>
        </nav>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider">
          <Home className="w-3.5 h-3.5" /> Guide pilier — Logement
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-tight">
          Logement étudiant en France 2026 : le guide complet
        </h1>
        <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed">
          Trouver un logement étudiant en France en 2026 peut sembler stressant : zones tendues, dossiers exigeants,
          arnaques en hausse, étudiants internationaux désorientés. Ce guide unique te résume <strong>TOUT</strong> ce
          qu'il faut savoir, étape par étape, pour décrocher ton logement sereinement — peu importe ton budget,
          ton statut, ou ta nationalité.
        </p>
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Mis à jour 14/05/2026</span>
          <span>·</span>
          <span>Lecture ~18 minutes</span>
          <span>·</span>
          <span>4 700 mots</span>
        </div>
        <div className="mt-7 rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
          <img src={cover} alt="Logement étudiant en France 2026" className="w-full h-auto" loading="eager" />
        </div>
      </section>

      {/* Table des matières */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-8" aria-label="Table des matières">
        <div className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-blue-50 to-white p-7">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-violet-600" />
            <p className="font-semibold text-violet-900 text-sm uppercase tracking-wider">Sommaire</p>
          </div>
          <ol className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-slate-700">
            <li><a className="hover:text-violet-700 hover:underline" href="#chercher">1. Comment trouver un logement étudiant</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#residences">2. Résidences étudiantes (CROUS + privées)</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#colocation">3. Colocation étudiante</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#visale">4. Visale : caution gratuite</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#dossierfacile">5. DossierFacile : ton dossier béton</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#apl">6. CAF / APL : 280 €/mois d'aide</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#arnaques">7. Éviter les arnaques</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#etrangers">8. Étudiants étrangers</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#checklist">9. Check-list logement complète</a></li>
            <li><a className="hover:text-violet-700 hover:underline" href="#faq">10. FAQ</a></li>
          </ol>
        </div>
      </section>

      {/* Section 1 - Chercher */}
      <Section id="chercher" icon={MapPin} title="1. Comment trouver un logement étudiant en France">
        <p>Trouver un logement étudiant en 2026 demande de la méthode. La concurrence est extrême dans les
        métropoles : en moyenne, <strong>une chambre étudiante à Paris se loue en moins de 48 heures</strong>.
        À Lyon, Bordeaux et Toulouse, compte 3 à 5 jours. La règle d'or : <strong>plus tu démarres tôt et
        plus ton dossier est complet, plus vite tu signes</strong>.</p>

        <H3>Les 4 voies principales</H3>
        <List items={[
          <><strong>1. CROUS</strong> (logement social étudiant) — Loyers de 175 à 500 €/mois. Dossier social étudiant (DSE) à constituer entre janvier et mai pour la rentrée. Aucun garant exigé.</>,
          <><strong>2. Résidences étudiantes privées</strong> (Nemea, Studea, Cardinal Campus, Studapart) — Loyers de 450 à 900 €/mois. Services inclus (Wi-Fi, ménage, salle commune). Bail mensuel possible.</>,
          <><strong>3. Logement classique en location</strong> (Leboncoin, PAP, SeLoger, agences) — Loyers de 380 à 1 500 €/mois selon ville et surface. Plus de liberté, plus de paperasse.</>,
          <><strong>4. Colocation</strong> (Roomlala, Appartager, groupes Facebook) — Loyers de 280 à 700 €/mois par personne. Idéal pour budget serré et vie sociale.</>,
        ]} />

        <H3>Quand commencer la recherche ?</H3>
        <p>Le calendrier optimal pour la rentrée de septembre :</p>
        <List items={[
          <><strong>Mars-Avril</strong> : ouverture DSE CROUS (deadline 31 mai). Première vague des résidences privées.</>,
          <><strong>Mai-Juin</strong> : fenêtre OR pour signer. Les bailleurs préfèrent verrouiller les dossiers avant l'été.</>,
          <><strong>Juillet</strong> : ralentissement (vacances bailleurs), retour des arnaques.</>,
          <><strong>Août-début septembre</strong> : rush + flambée des prix. Évite si possible.</>,
          <><strong>Septembre-octobre</strong> : opportunités secondes vagues (désistements, mutations).</>,
        ]} />

        <Callout title="📌 Voir la méthode 7 jours">
          <p>Tu démarres ta recherche maintenant ? On a écrit un guide étape par étape : <Link to="/blog/trouver-logement-etudiant-rapidement-2026" className="text-violet-700 underline font-medium">Trouver un logement étudiant en 7 jours</Link>. Plan jour par jour, scripts à copier-coller, anti-arnaques.</p>
        </Callout>
      </Section>

      {/* Section 2 - Résidences étudiantes */}
      <Section id="residences" icon={Building2} title="2. Résidences étudiantes : CROUS et privées">
        <H3>Résidences CROUS (publiques)</H3>
        <p>Le <strong>Centre Régional des Œuvres Universitaires et Scolaires</strong> gère plus de 175 000 logements
        étudiants en France. Ces logements sont les <strong>plus abordables du marché</strong> : à partir de
        175 €/mois pour une chambre traditionnelle, jusqu'à 500 €/mois pour un studio rénové. Aucun garant
        n'est exigé, l'inscription se fait via le DSE (Dossier Social Étudiant) sur
        messervices.etudiant.gouv.fr.</p>
        <List items={[
          "Constitue ton DSE entre le 1ᵉʳ mars et le 31 mai (pour la rentrée de septembre)",
          "Joins la simulation de bourse + tes notes du Bac/des derniers semestres",
          "Indique 4 vœux d'académies maximum",
          "Le CROUS te notifie ta proposition de juin à juillet",
        ]} />
        <Callout title="📚 Approfondir le sujet CROUS">
          <p>Articles satellites du silo CROUS : <Link to="/blog/trouver-logement-crous-2026" className="text-violet-700 underline">Guide complet logement CROUS</Link>.</p>
        </Callout>

        <H3>Résidences étudiantes privées</H3>
        <p>Les principales chaînes en France :</p>
        <List items={[
          <><strong>Nemea, Cardinal Campus, Réside Études</strong> : résidences services premium (450-900 €/mois). Wi-Fi, ménage, salle de sport.</>,
          <><strong>Studea, Studapart</strong> : volumes énormes, bail flexible 1 à 12 mois. Idéal Erasmus.</>,
          <><strong>Suitétudes, Twenty Campus</strong> : récentes, design moderne, communautés étudiantes.</>,
          <><strong>Adèle.org</strong> : annuaire de référence des résidences privées en France.</>,
        ]} />
        <p>Avantages : pas de garant nécessaire, bail mensuel, ambiance étudiante. Inconvénients : loyer plus
        élevé, charges parfois opaques, atmosphère « hotellerie ».</p>
      </Section>

      {/* Section 3 - Colocation */}
      <Section id="colocation" icon={Users} title="3. Colocation étudiante : l'option budget malin">
        <p>La colocation reste l'option la plus rentable pour les étudiants : tu divises le loyer, les charges
        et l'internet par 2 à 4. Bonus social : tu rencontres du monde. À Paris, une chambre en colocation
        coûte 450-650 €/mois (vs 750-950 € pour un studio). À Lyon : 350-450 €/mois.</p>

        <H3>Les 3 types de baux en colocation</H3>
        <List items={[
          <><strong>Bail unique solidaire</strong> : tous les colocataires sur le même bail, solidaires si un colocataire ne paie pas. Le plus courant — exige confiance entre colocs.</>,
          <><strong>Baux individuels</strong> : chaque coloc a son propre bail (sa chambre + accès parties communes). Plus protecteur mais plus rare.</>,
          <><strong>Bail mobilité</strong> : 1 à 10 mois non renouvelable, pour étudiants en stage, alternance, mobilité. Souple mais pas de droit au renouvellement.</>,
        ]} />

        <H3>Trouver une colocation</H3>
        <List items={[
          "Roomlala (privilégie les comptes vérifiés)",
          "Appartager (gros volume, attention aux annonces fantômes)",
          "Leboncoin filtre « colocation »",
          "Groupes Facebook spécifiques par ville (« Colocation Paris », « Coloc Lyon »…)",
          "BDE de ton école / fac (souvent des annonces qualifiées)",
        ]} />
      </Section>

      {/* Section 4 - Visale */}
      <Section id="visale" icon={ShieldCheck} title="4. Visale : la caution publique gratuite (incontournable)">
        <p><a href="https://www.visale.fr" target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">Visale</a> est
        la garantie publique d'Action Logement. Si ton bailleur refuse de te louer faute de garant solvable,
        Visale prend le relais : elle <strong>garantit jusqu'à 36 mois de loyers impayés</strong> et
        accompagne ta caution. C'est gratuit, accessible aux 18-30 ans, et acceptée par <strong>90 %</strong>
        des bailleurs étudiants.</p>

        <H3>Qui peut bénéficier de Visale ?</H3>
        <List items={[
          "Étudiants, alternants, jeunes actifs de moins de 30 ans",
          "Salariés du privé en CDD, intérim, période d'essai (jeunes ou non)",
          "Étudiants étrangers de 18 à 30 ans avec titre de séjour valide",
          "Personnes en mobilité géographique pour stage ou études",
        ]} />

        <H3>Comment activer Visale en 10 minutes</H3>
        <List items={[
          "Va sur visale.fr et clique sur « Demander un visa Visale »",
          "Renseigne tes informations (identité, ressources)",
          "Valide en ligne (sous 1-2 jours)",
          "Reçois ton « visa Visale numérique » à présenter aux bailleurs",
          "Présente-le à chaque visite — argument décisif pour signer rapidement",
        ]} />

        <Callout title="🔗 Approfondir le sujet Visale">
          <p>Article satellite (à venir) : <span className="text-slate-500">Visale 2026 : le guide complet caution gratuite (P1 — semaine 5)</span>.</p>
        </Callout>
      </Section>

      {/* Section 5 - DossierFacile */}
      <Section id="dossierfacile" icon={FileText} title="5. DossierFacile : ton dossier locatif béton">
        <p><a href="https://www.dossierfacile.fr" target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">DossierFacile.fr</a> est
        le service officiel de l'État qui certifie ton dossier locatif. Tu uploades tes pièces une seule fois,
        l'État vérifie tout et te délivre un dossier « certifié » que tu envoies aux bailleurs en 1 clic.</p>

        <H3>Pourquoi c'est game-changer</H3>
        <List items={[
          "Bailleurs te font 3x plus confiance (dossier vérifié par l'État)",
          "Pas de risque qu'on perde ou fasse fuiter tes documents",
          "Mise à jour rapide en cas de changement de situation",
          "Étudiants français ET étrangers acceptés",
          "100 % gratuit, sans publicité, sans intermédiaire commercial",
        ]} />

        <H3>Pièces à scanner</H3>
        <List items={[
          "Pièce d'identité recto-verso (CNI ou passeport)",
          "Attestation d'inscription dans ton établissement",
          "3 derniers avis de virement (bourse, salaire, alternance) OU attestation parents garants",
          "2 derniers avis d'imposition (toi ou garants)",
          "RIB français à ton nom",
          "Justificatif de domicile actuel",
        ]} />

        <Callout title="🔗 Approfondir le sujet DossierFacile">
          <p>Article satellite (à venir) : <span className="text-slate-500">DossierFacile : mode d'emploi étudiant en 10 minutes (P1 — semaine 6)</span>.</p>
        </Callout>
      </Section>

      {/* Section 6 - CAF / APL */}
      <Section id="apl" icon={Wallet} title="6. CAF / APL : jusqu'à 280 €/mois d'aide au logement">
        <p>L'<strong>Aide Personnalisée au Logement (APL)</strong> est versée par la CAF aux étudiants
        locataires. Selon ton loyer, ta zone géographique et ta situation, elle peut atteindre
        <strong> 280 €/mois</strong>. C'est l'aide la plus impactante pour le budget étudiant. Cumulable avec
        bourse CROUS, AAH, alternance.</p>

        <H3>Les 3 conditions essentielles</H3>
        <List items={[
          "Avoir un bail à TON nom (ou être colocataire signataire)",
          "Habiter ton logement en résidence principale (8+ mois/an)",
          "Avoir des ressources modestes (la CAF calcule selon ton revenu fiscal + celui de tes parents si rattaché)",
        ]} />

        <H3>Comment estimer ton APL avant de signer</H3>
        <p>Va sur le <a href="https://wwwd.caf.fr/wps/portal/caffr/aidesetdemarches/lesservicesenligne/estimervosdroits/leslogementsalsa" target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">simulateur officiel CAF</a> et
        renseigne loyer + zone + situation. En 2 minutes tu sais combien tu toucheras. Très utile pour
        choisir entre 2 logements.</p>

        <Callout title="🔗 Articles satellites du silo APL">
          <p>
            <Link to="/blog/demande-apl-etudiant-2026" className="text-violet-700 underline">Demande d'APL étudiant 2026 : guide pas-à-pas</Link>
            {" · "}
            <Link to="/blog/caf-etudiant-etranger-france-2026" className="text-violet-700 underline">CAF étudiant étranger : APL/ALS</Link>
          </p>
        </Callout>
      </Section>

      {/* Section 7 - Arnaques */}
      <Section id="arnaques" icon={AlertTriangle} title="7. Éviter les arnaques au logement étudiant">
        <p>Les arnaques au logement étudiant ont <strong>doublé en 2025</strong>. Les profils étudiants
        (jeunes, pressés, parfois en visio depuis l'étranger) sont une cible facile. Voici les 5 arnaques
        les plus fréquentes.</p>

        <H3>Les 5 arnaques classiques</H3>
        <List items={[
          <><strong>1. Le faux propriétaire « à l'étranger »</strong> : annonce alléchante, propriétaire « en Espagne pour le travail », demande virement Western Union de la caution. <strong>Arnaque 100 % du temps</strong>.</>,
          <><strong>2. Le bail signé sans visite</strong> : pression à signer en urgence sans visite physique. Refuse systématiquement.</>,
          <><strong>3. L'annonce dupliquée</strong> : la même photo apparaît à 3 prix différents sur 3 sites. Le « propriétaire » te répond pour chacune. Vérifie la photo via Google Images.</>,
          <><strong>4. Le faux CROUS</strong> : site qui imite l'identité visuelle du CROUS, demande paiement pour valider un logement (le CROUS ne te demande JAMAIS de payer avant signature).</>,
          <><strong>5. Les charges « divers » occultes</strong> : bail qui mentionne 80 €/mois de « charges diverses » sans détail. Illégal en location nue. Exige le détail (loi du 6 juillet 1989).</>,
        ]} />

        <H3>La règle des 3 ✅"</H3>
        <List items={[
          "✅ Visite physique avant signature (jamais à distance sans preuve vidéo)",
          "✅ Identité du bailleur vérifiable (extrait Kbis si société, copie pièce d'identité si particulier)",
          "✅ Paiement uniquement après signature physique du bail",
        ]} />

        <Callout title="🔗 Approfondir le sujet anti-arnaques">
          <p>Article satellite (à venir) : <span className="text-slate-500">12 arnaques logement étudiant 2026 : repérage en 30 secondes (P0 — semaine 2)</span>.</p>
        </Callout>
      </Section>

      {/* Section 8 - Étudiants étrangers */}
      <Section id="etrangers" icon={Globe2} title="8. Étudiants étrangers : louer en France">
        <p>Tu es <strong>étudiant étranger</strong> (Erasmus, mobilité internationale, non-UE) ? Bonne
        nouvelle : tu peux louer en France comme un étudiant français, à condition d'avoir un
        <strong>titre de séjour valide</strong> (VLS-TS étudiant validé sur l'ANEF).</p>

        <H3>Les options par ordre de simplicité</H3>
        <List items={[
          <><strong>1. CROUS</strong> : zéro arnaque, dossier simplifié, accepte tous les étudiants internationaux.</>,
          <><strong>2. Résidences privées internationales</strong> (Studapart, Nemea Erasmus) : interface en anglais, bail mensuel possible.</>,
          <><strong>3. Logement classique avec Visale</strong> : accessible aux 18-30 ans avec titre de séjour valide.</>,
          <><strong>4. Colocation Erasmus</strong> : forum Erasmus + groupes Facebook par ville, vie sociale garantie.</>,
        ]} />

        <Callout title="🔗 Articles satellites du silo International">
          <p>
            <Link to="/blog/etudiant-international-erasmus-france-2026" className="text-violet-700 underline">Guide étudiant international</Link>
            {" · "}
            <Link to="/blog/visa-etudiant-france-2026-guide-complet" className="text-violet-700 underline">Visa étudiant France</Link>
            {" · "}
            <Link to="/blog/caf-etudiant-etranger-france-2026" className="text-violet-700 underline">CAF étudiant étranger</Link>
            {" · "}
            <Link to="/en/students" className="text-violet-700 underline">English version</Link>
          </p>
        </Callout>
      </Section>

      {/* Section 9 - Checklist */}
      <Section id="checklist" icon={ListChecks} title="9. Check-list logement étudiant : les 15 démarches">
        <p>Imprime cette checklist ou ajoute-la à ton compte Student Hub. Elle te suit jusqu'à la remise des clés.</p>
        <ol className="my-5 space-y-2.5 text-slate-700">
          {[
            "Constituer mon DossierFacile (gratuit, public)",
            "Activer Visale (gratuit, accepté à 90 %)",
            "Constituer mon DSE CROUS (entre janv et mai)",
            "Définir mon budget max (loyer + charges + APL)",
            "Créer mes alertes sur les 6 plateformes (Leboncoin, PAP, SeLoger, Studapart, Adele, Roomlala)",
            "Réserver mes visites par batch de 3-5 par jour",
            "Prendre des photos détaillées de chaque pièce",
            "Vérifier le DPE (refuser logements F et G)",
            "Lire le bail intégralement avant signature",
            "Souscrire une assurance habitation (3-8 €/mois)",
            "Faire un état des lieux détaillé avec photos",
            "Demander le récap CAF de mon nouveau logement",
            "Demander mon APL sur caf.fr (sous 5 jours après signature)",
            "Configurer mes prélèvements EDF/GDF/internet",
            "Déclarer mon changement d'adresse à mon université, sécu, banque, impôts",
          ].map((task, i) => (
            <li key={i} className="flex gap-3 leading-relaxed">
              <input type="checkbox" className="w-4 h-4 mt-1 accent-violet-600" aria-label={task} />
              <span>{task}</span>
            </li>
          ))}
        </ol>
        <Callout title="🚀 Génère ta checklist personnalisée">
          <p>Sur <Link to="/" className="text-violet-700 underline font-medium">Student Hub</Link>,
          l'onglet « Démarches » génère ta checklist EN FONCTION de ta situation (étudiant français, étranger,
          alternant, boursier). Tu coches au fur et à mesure et tu reçois des rappels automatiques.</p>
        </Callout>
      </Section>

      {/* Section 10 - FAQ */}
      <Section id="faq" icon={FileText} title="10. FAQ logement étudiant 2026">
        <Q q="Combien coûte un logement étudiant en France en 2026 ?" a="Le loyer moyen d'un étudiant en France en 2026 est de 480 €/mois en province et 750 €/mois à Paris (charges comprises). Un studio CROUS démarre à 175 €/mois, un T1 privé en province à 380 €, un studio à Paris à 700 €. L'APL peut réduire ce coût de 100 à 280 €." />
        <Q q="Quand commencer à chercher son logement étudiant ?" a="Pour septembre, démarre dès mai (résidences CROUS et privées ouvrent leurs réservations). Pour les zones tendues (Paris, Lyon, Bordeaux), commence en avril. En cours d'année, mi-décembre et fin juin sont les meilleures fenêtres (départs Erasmus, fins de baux)." />
        <Q q="Peut-on louer un logement étudiant sans garant ?" a="Oui. Active la garantie Visale gratuite (Action Logement) : elle remplace le garant pour 90 % des bailleurs. Le CROUS n'exige pas de garant. Alternatives payantes : Garantme, SmartGarant, Cautioneo. Ou bloque une caution bancaire." />
        <Q q="Quel est le délai pour toucher l'APL étudiant ?" a="Compte 4 à 6 semaines entre le dépôt de ta demande CAF et le premier versement. L'APL est rétroactive au mois de demande. Délai plus long (8-12 semaines) pour les étudiants étrangers." />
        <Q q="Comment éviter les arnaques au logement étudiant ?" a="Visite toujours physiquement avant de signer. Ne paie JAMAIS le dépôt ou les frais avant la signature physique du bail. Vérifie le DPE, l'identité du bailleur. Ignore les annonces avec propriétaire « à l'étranger » ou paiement Western Union." />
        <Q q="Un étudiant étranger peut-il louer en France ?" a="Oui, à condition d'avoir un titre de séjour valide (VLS-TS étudiant validé sur l'ANEF). Visale est accessible aux étudiants étrangers de 18 à 30 ans. Le CROUS accueille tous les étudiants internationaux." />
        <Q q="Quels sont les frais à prévoir en plus du loyer ?" a="Provision pour charges (eau, ascenseur, ordures, entretien — 30 à 100 €/mois selon le logement), assurance habitation (3-8 €/mois), électricité/gaz (50-120 €/mois studio), internet (15-35 €/mois). Total : 100-250 €/mois en plus du loyer." />
        <Q q="Quelles villes ont les logements étudiants les moins chers ?" a="Saint-Étienne (250 €/mois studio), Reims (290 €), Le Mans (310 €), Brest (320 €), Limoges (320 €). Les plus chères : Paris (750 €), Lyon (550 €), Bordeaux (520 €), Nice (510 €), Toulouse (480 €)." />
      </Section>

      {/* CTA bottom */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-blue-600 p-10 sm:p-14 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,white,transparent_70%)]" aria-hidden="true" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Prêt·e à signer ton logement ?</h2>
            <p className="text-white/90 leading-relaxed mb-7 text-lg max-w-2xl">
              Crée ton compte Student Hub (gratuit). Tu génères ton dossier locatif certifié en 5 minutes,
              tu reçois des alertes anti-arnaques temps-réel, et tu accèdes à la communauté étudiante
              traduisible en 8 langues — celle qui partage chaque jour les meilleures annonces.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" data-testid="pillar-logement-cta-bottom-primary">
                <Button size="lg" className="bg-white text-violet-700 hover:bg-violet-50 font-semibold rounded-full px-8">
                  Créer mon compte gratuit <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/blog/trouver-logement-etudiant-rapidement-2026" data-testid="pillar-logement-cta-bottom-secondary">
                <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 rounded-full px-8 border border-white/30">
                  Méthode 7 jours →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="border-t border-slate-200 py-10 text-center text-sm text-slate-500">
        <p>Student Hub France · L'app étudiante française de référence</p>
        <p className="mt-2 space-x-4">
          <Link to="/" className="hover:text-violet-700">Accueil</Link>
          <Link to="/blog" className="hover:text-violet-700">Blog</Link>
          <Link to="/landing/etudiants" className="hover:text-violet-700">Espace étudiants</Link>
          <Link to="/en/students" className="hover:text-violet-700">English</Link>
        </p>
      </footer>
    </div>
  );
}

/** Helpers visuels — gardent le composant principal lisible et stable. */
function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-5 sm:px-8 py-10 scroll-mt-24">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
        <Icon className="w-7 h-7 text-violet-600 shrink-0" />
        <span>{title}</span>
      </h2>
      <div className="text-slate-700 leading-relaxed space-y-4 text-[17px]">{children}</div>
    </section>
  );
}

function H3({ children }) {
  return <h3 className="font-display text-lg font-bold text-slate-900 mt-6 mb-2">{children}</h3>;
}

function List({ items }) {
  return (
    <ul className="my-4 space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 leading-relaxed">
          <span className="text-violet-500 font-bold shrink-0">·</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Callout({ title, children }) {
  return (
    <aside className="my-6 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 p-5">
      <p className="font-semibold text-violet-900 text-sm mb-1.5">{title}</p>
      <div className="text-sm text-violet-900/85 leading-relaxed">{children}</div>
    </aside>
  );
}

function Q({ q, a }) {
  return (
    <details className="rounded-2xl border border-slate-200 p-4 my-3 group">
      <summary className="cursor-pointer font-semibold text-slate-900 list-none flex items-center justify-between">
        <span>{q}</span>
        <span className="text-violet-500 group-open:rotate-45 transition-transform text-xl">+</span>
      </summary>
      <p className="mt-3 text-slate-700 leading-relaxed">{a}</p>
    </details>
  );
}
